// /src/lib/globalAgGrid.js
import React, { Component } from "react";
import { AgGridReact } from "ag-grid-react";
import { cloneDeep, isObject } from "lodash";
import { toast } from "react-toastify";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "ag-grid-community";
import "../style/agGrid.css";
import "ag-grid-enterprise";
import {
  LicenseManager,
  CellSelectionModule,
  ColumnMenuModule,
  ContextMenuModule,
  SetFilterModule,
  CustomEditorModule,
  ExcelExportModule,
  ClipboardModule,
  RowNumbersModule,
} from "ag-grid-enterprise";
import {
  ModuleRegistry,
  provideGlobalGridOptions,
  ClientSideRowModelModule,
  ColumnAutoSizeModule,
  RowApiModule,
  RowSelectionModule,
  ExternalFilterModule,
  TextEditorModule,
  ColumnApiModule,
  TextFilterModule,
  CellStyleModule,
  ScrollApiModule,
  ClientSideRowModelApiModule,
  NumberEditorModule,
  RowStyleModule,
  ROW_NUMBERS_COLUMN_ID,
  enableDevValidations,
} from "ag-grid-community";
import { DateFilterModule, NumberFilterModule } from "ag-grid-community";
import { ColumnsToolPanelModule, FiltersToolPanelModule } from "ag-grid-enterprise";

// AG Grid v36 no longer bundles ValidationModule, so without this the console only
// shows bare error codes. Must run before any grid is created.
if (process.env.NODE_ENV !== "production" && process.env.REACT_APP_AG_GRID_VALIDATIONS === "true") {
  enableDevValidations();
}

ModuleRegistry.registerModules([
  ColumnAutoSizeModule,
  RowApiModule,
  ScrollApiModule,
  CellSelectionModule,
  CellStyleModule,
  RowSelectionModule,
  ColumnMenuModule,
  ContextMenuModule,
  ExternalFilterModule,
  SetFilterModule,
  CustomEditorModule,
  TextEditorModule,
  ColumnApiModule,
  ExcelExportModule,
  TextFilterModule,
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ClipboardModule,
  NumberFilterModule,
  DateFilterModule,
  ClientSideRowModelApiModule,
  NumberEditorModule,
  RowNumbersModule,
  RowStyleModule,
]);

// Mark all grids as using legacy themes
provideGlobalGridOptions({
  theme: "legacy",
});
const gridModules = [ClientSideRowModelModule];

LicenseManager.setLicenseKey(
  "Using_this_{AG_Grid}_Enterprise_key_{AG-083969}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{TELUS_Agriculture_&_Consumer_Goods_(US,_PA,_6632)}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{TABSInsight}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{TABSInsight}_need_to_be_licensed___{TABSInsight}_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{14_October_2026}____[v3]_[01]_MTc5MTkzMjQwMDAwMA==073ec8e13184eaa9da8a95a5efbcc8c5",
);
// import {
//   POS_UNITS,
//   CUME_OVER_UNDER,
//   INCREMENTAL_LIFT_UNITS,
//   SHIP_UNITS
// } from "../constants";
//import PercChangeModal from "./percChangeModal";

export function checkForImmutableCell(_this, editable, curRow) {
  //Do not edit cells that are non editable
  if (editable === undefined) {
    return false;
  }
  return !editable;
}

// Row-level actions (Copy Row, Delete Row) key off row selection (getSelectedNodes),
// but a click-and-drag across cells builds a cell range — a SEPARATE selection state.
// A plain click always row-selects the clicked row as a side effect, so by the time a
// drag finishes, getSelectedNodes() already has exactly that one anchor row, never
// empty — so a row-level action naively using getSelectedNodes() alone only ever acts
// on that one row, not the rows the user visibly dragged over.
//
// When the cell range spans MORE rows than the current row selection already covers,
// select every row the range spans (mutating real row selection) and return true so
// the caller knows to re-read getSelectedNodes(). Row-selection methods that already
// cover every intended row (click / Shift+click / Ctrl+click) are left untouched.
export function preferRangeOverSelection(api, currentSelectedCount) {
  const ranges = api.getCellRanges();
  if (!ranges || ranges.length === 0) return false;
  const rowIndexes = new Set();
  ranges.forEach((range) => {
    const start = range.startRow?.rowIndex;
    const end = range.endRow?.rowIndex;
    if (start === undefined || start === null || end === undefined || end === null) return;
    const lo = Math.min(start, end);
    const hi = Math.max(start, end);
    for (let i = lo; i <= hi; i++) rowIndexes.add(i);
  });
  if (rowIndexes.size <= currentSelectedCount) return false;
  // node.setSelected() dispatches a selectionChanged event (which rebuilds
  // getSelectedNodes() from the full selection map) on every single call, so
  // calling it once per row here is O(n) dispatches each doing O(selection-size)
  // work — effectively O(n^2) for a whole-table range, which can hang the tab
  // on a large grid. api.setNodesSelected() batches an array into one dispatch.
  const nodesToSelect = [];
  rowIndexes.forEach((rowIndex) => {
    const node = api.getDisplayedRowAtIndex(rowIndex);
    if (node && !node.isSelected()) nodesToSelect.push(node);
  });
  if (nodesToSelect.length > 0) api.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  return true;
}

// "Paste Row" snapshot — deliberately module-scoped (not component state), so it survives
// a GlobalAgGrid remount when the user switches tables (dmGrid.js keys the grid by table
// id) the same way the system clipboard would. Holds the actual copied row data objects
// (not clipboard text) plus the source grid's column layout, so pasting can align values
// by field name instead of by clicked cell position, and can be gated off when the
// destination grid's columns don't match the table the rows were copied from.
let lastCopiedRowSnapshot = null;

export function getRowLayoutColumnIds(api) {
  return api
    .getAllDisplayedColumns()
    .filter((c) => c.getColId() !== ROW_NUMBERS_COLUMN_ID)
    .map((c) => c.getColId());
}

// Intentionally order-independent — compares which columns are present, not their
// display order. pasteRow aligns each value by column id/field, not by position, so
// two grids with the same columns in a different display order are still a valid
// paste target; only a difference in the actual set of columns should disable it.
export function sameRowLayout(columnIdsA, columnIdsB) {
  if (!columnIdsA || !columnIdsB || columnIdsA.length !== columnIdsB.length) return false;
  const signature = (ids) => [...ids].sort().join(" ");
  return signature(columnIdsA) === signature(columnIdsB);
}

// Same markup/classes ag-grid's own built-in "copy"/"paste" context menu items render
// (ag-icon-copy / ag-icon-paste, from the "clipboardCopy"/"clipboardPaste" icon set —
// already available since ag-grid.css is imported above), so our custom Copy Row /
// Paste / Paste Row items look consistent with the built-in ones instead of icon-less.
const clipboardCopyIcon = "<span class='ag-icon ag-icon-copy' role='presentation' unselectable='on'></span>";
const clipboardPasteIcon = "<span class='ag-icon ag-icon-paste' role='presentation' unselectable='on'></span>";
const excelExportIcon = "<span class='ag-icon ag-icon-excel' role='presentation' unselectable='on'></span>";

export function changeByPercent() {
  //show change by % modal
  this.handleToggleModal();
}
export function increaseXPercent(percent) {
  if (this.gridApi.rangeController.isEmpty()) {
    return;
  }

  var _this = this;
  var rangeSelections = this.gridApi.rangeController.getCellRanges();
  var beginRow = rangeSelections[0].startRow;
  var beginRowIdx = beginRow.rowIndex;
  var endRow = rangeSelections[0].endRow;
  var endRowIdx = endRow.rowIndex;
  let curRowIdx = beginRowIdx;

  rangeSelections[0].columns.forEach((value, index) => {
    while (curRowIdx <= endRowIdx) {
      let colId = value.colId;
      if (checkForImmutableCell(this, value.pinned, beginRowIdx)) {
        //do Nothing
      } else {
        let fillValue = parseFloat(_this.gridApi.getRowNode(curRowIdx).data[value.colId], 10);
        _this.gridApi.getRowNode(curRowIdx).setDataValue(colId, fillValue + fillValue * parseFloat(percent, 10));
      }
      curRowIdx = curRowIdx + 1;
    }
    curRowIdx = beginRowIdx;
  });
}

export function fillAcross() {
  // Check if gridApi is initialized
  if (!this.gridApi) {
    console.error("Grid API is not initialized.");
    return;
  }

  // Get the selected cell ranges
  const cellRanges = this.gridApi.getCellRanges();
  if (!cellRanges || cellRanges.length === 0) {
    return;
  }

  // Assume only the first range is relevant, as in the original
  const rangeSelections = cellRanges;
  const beginRow = rangeSelections[0].startRow;
  const beginRowIdx = beginRow.rowIndex;
  const endRow = rangeSelections[0].endRow;
  const endRowIdx = endRow.rowIndex;

  // Proceed only if the selection is within a single row
  if (beginRowIdx !== endRowIdx) {
    return;
  }

  // Get the starting column and row node
  const startCol = rangeSelections[0].startColumn;
  const rowNode = this.gridApi.getDisplayedRowAtIndex(beginRowIdx);
  if (!rowNode) {
    console.error("Row node not found.");
    return;
  }

  // Get the value to fill from the starting cell
  let fillValue = rowNode.data[startCol.colDef.field];

  // Get all columns in the order they are defined
  const allColumns = this.gridApi.getColumns();
  let startFilling = false;

  // Iterate through columns, starting from the selected column
  allColumns.forEach((col) => {
    if (col.getColId() === startCol.getColId()) {
      startFilling = true;
    }
    if (startFilling) {
      const colId = col.getColId();
      if (checkForImmutableCell(this, col.getColDef().editable, beginRowIdx)) {
        // Skip immutable cells
      } else {
        // Handle object values, extracting 'name' if not using FKCellRenderer
        let valueToSet = fillValue;
        if (typeof valueToSet === "object" && col.getColDef().cellRenderer !== "FKCellRenderer") {
          valueToSet = valueToSet.name;
        }
        rowNode.setDataValue(colId, valueToSet);
      }
    }
  });
}

export function excelExport() {
  function formattingFunction(params) {
    if (params.value === undefined) {
      return "";
    }
    if (params.column.colDef.cellRenderer === undefined) {
      if (params.column.colDef.context?.columnType === "VARCHAR") {
        return "'" + params.value; // Force text format in Excel to keep leading zero
      }

      return params.value;
    } else if (params.column.colDef.cellRenderer === "FKCellRenderer") {
      if (params.value === null) {
        return "NULL";
      } else if (params.value.name === undefined) {
        if (isObject(params.value)) {
          return "";
        }
        return params.value;
      } else {
        return params.value.name;
      }
    } else {
      return params.value;
    }
  }
  let excelParams = {
    processCellCallback: formattingFunction,
  };
  this.gridApi.exportDataAsExcel(excelParams);
}
export function fillSelection() {
  // Ensure gridApi is initialized
  if (!this.gridApi) {
    console.error("Grid API is not initialized.");
    return;
  }

  // Get the current cell ranges (replaces rangeController)
  const cellRanges = this.gridApi.getCellRanges();

  // Check if there are no selected ranges (replaces isEmpty())
  if (!cellRanges || cellRanges.length === 0) {
    return;
  }

  const _this = this;
  const rangeSelections = cellRanges;
  const beginRow = rangeSelections[0].startRow;
  const beginRowIdx = beginRow.rowIndex;
  const endRow = rangeSelections[0].endRow;
  const endRowIdx = endRow.rowIndex;
  let curRowIdx = beginRowIdx;

  // Use getDisplayedRowAtIndex to access row data
  const fillValue = this.gridApi.getDisplayedRowAtIndex(beginRowIdx).data[rangeSelections[0].columns[0].colDef.field];

  rangeSelections[0].columns.forEach((value, index) => {
    while (curRowIdx <= endRowIdx) {
      const colId = value.getColId(); // Use getColId() for column ID
      if (checkForImmutableCell(this, value.getColDef().editable, beginRowIdx)) {
        // Do nothing for immutable cells
      } else {
        let valueToFill = fillValue;
        if (typeof valueToFill === "object" && value.getColDef().cellRenderer !== "FKCellRenderer") {
          valueToFill = valueToFill.name;
        }
        // Update the cell value using setDataValue
        _this.gridApi.getDisplayedRowAtIndex(curRowIdx).setDataValue(colId, valueToFill);
      }
      curRowIdx = curRowIdx + 1;
    }
    curRowIdx = beginRowIdx;
  });
}
export function fillDown() {
  // Check if the grid API is initialized
  if (!this.gridApi) {
    console.error("Grid API is not initialized.");
    return;
  }

  // Check if there are any selected cell ranges
  const cellRanges = this.gridApi.getCellRanges();
  if (!cellRanges || cellRanges.length === 0) {
    return;
  }

  // Get the starting row from the first range selection
  const rangeSelections = cellRanges;
  let beginRow;
  if (rangeSelections[0].startRow) {
    beginRow = rangeSelections[0].startRow;
  } else {
    beginRow = rangeSelections[0].start; // Fallback for older versions
  }

  const beginRowIdx = beginRow.rowIndex;
  const endRow = this.gridApi.getDisplayedRowCount();

  let curRow = beginRowIdx;
  let fillValue;

  // Get the fill value from the starting row
  const startRowNode = this.gridApi.getDisplayedRowAtIndex(beginRowIdx);
  if (startRowNode) {
    const colId = rangeSelections[0].columns[0].getColId();
    fillValue = parseFloat(startRowNode.data[colId], 10);
  } else {
    console.error("Start row node not found.");
    return;
  }

  // Fill down the values
  while (curRow < endRow) {
    if (checkForImmutableCell(this, rangeSelections[0].columns[0].getColDef().pinned, curRow)) {
      // Do nothing for immutable cells
    } else {
      const rowNode = this.gridApi.getDisplayedRowAtIndex(curRow);
      if (rowNode) {
        const colId = rangeSelections[0].columns[0].getColId();
        rowNode.setDataValue(colId, fillValue);
      }
    }
    curRow = curRow + 1;
  }
}

export class GlobalAgGrid extends Component {
  constructor(props) {
    super(props);
    this.fillAcross = fillAcross;
    // this.fillDown = fillDown;
    this.increaseXPercent = increaseXPercent;
    this.fillSelection = fillSelection;
    this.excelExport = excelExport;
    this.changeByPercent = changeByPercent;
    //this.handleToggleModal = this.handleToggleModal.bind(this);
    this.state = {
      //showModal: false
    };
    this._pasteUpdates = []; // Track direct data writes during clipboard paste
    this.agGrid = React.createRef();
    this.gridContainerRef = React.createRef();
    this.gridElement = null;
  }

  componentDidMount() {
    if (this.gridContainerRef.current) {
      this.gridContainerRef.current.addEventListener("keydown", this.handleNativeKeyDown, true);
    }
  }
  componentWillUnmount() {
    if (this.gridContainerRef.current) {
      this.gridContainerRef.current.removeEventListener("keydown", this.handleNativeKeyDown, true);
    }
    if (process.env.NODE_ENV !== "production" && window.__mdmGrid) {
      window.__mdmGrid = null;
    }
    if (this.gridApi) {
      this.gridApi.destroy();
    }
  }

  // Row Numbers only ever produces a cell-range selection (getCellRanges()), never AG
  // Grid's row selection (getSelectedNodes()) — see below for why that matters. Detecting
  // "did this drag start on the row-number gutter" via a native mousedown listener
  // doesn't work: AG Grid's own internal row-number handling (RangeService.handleCellMouseDown
  // calls event.preventDefault() and apparently swallows propagation for row-number cells
  // specifically) never lets the event reach ANY listener, not even one on document — verified
  // that an identical listener fires normally for ordinary data cells but never for row-number
  // cells. Detect it structurally instead: AG Grid explicitly builds a row-number-driven range
  // to span every displayed data column. This is a heuristic, not a certainty — reading AG
  // Grid's own source (RowNumbersService.isAllColumnsRange) shows it uses the exact same
  // shape-based check internally, and confirms there is no other reliable, publicly-queryable
  // origin signal: a range's startColumn is not a dependable discriminator either — a genuine
  // row-number-driven drag can produce a CellRange whose startColumn is still the literal
  // row-numbers column (confirmed live: getCellRanges() returned exactly that mid-drag), so
  // checking it against the leftmost data column rejects real row-number ranges as often as it
  // rules out false positives. So an ordinary data-cell drag that happens to span every
  // displayed column on a narrow table (few columns) is indistinguishable from a row-number
  // drag by the time getCellRanges() returns it, and will also get mirrored into row selection
  // here. The existing Delete Row confirmation modal (dmGridContainer.js), which states the
  // exact row count before deleting, is the practical safety net for that edge case — a user
  // who only meant to copy a few cells would see an unexpectedly large "delete N rows?" count
  // and can cancel.
  isWholeRowRange = (api, range) => {
    const dataColumns = api.getAllDisplayedColumns().filter((c) => c.getColId() !== ROW_NUMBERS_COLUMN_ID);
    return dataColumns.length > 0 && range.columns.length === dataColumns.length;
  };

  // Row/cell delete and the "Copy Row" context-menu action key off getSelectedNodes()
  // (real row selection), but dragging across the row-number gutter only ever builds a
  // cell range (getCellRanges()) per AG Grid's design — the two selection states are
  // otherwise never in sync. Mirror row-number-driven range selection into real row
  // selection here so Delete/Copy Row act on the rows the user visibly selected.
  // Ordinary cell-range selection (started on a data cell) is left alone: it must not
  // silently select rows for deletion just because the drag happened to cross them, and
  // this app's normal row-click selection already replaces the prior selection on its own.
  onCellSelectionChanged = (event) => {
    if (!event.finished) return;
    const api = event.api;
    const ranges = (api.getCellRanges() || []).filter((range) => this.isWholeRowRange(api, range));
    if (ranges.length === 0) return;
    const selectedRowIndexes = new Set();
    ranges.forEach((range) => {
      const start = range.startRow?.rowIndex;
      const end = range.endRow?.rowIndex;
      if (start === undefined || end === undefined) return;
      const lo = Math.min(start, end);
      const hi = Math.max(start, end);
      for (let i = lo; i <= hi; i++) selectedRowIndexes.add(i);
    });
    // node.setSelected() dispatches a selectionChanged event (which rebuilds
    // getSelectedNodes() from the full selection map) on every single call, so
    // calling it once per row via forEachNode is O(n) dispatches each doing
    // O(selection-size) work — effectively O(n^2) for a whole-table drag. That's
    // what produced a live "Paused before potential out-of-memory crash" Chrome
    // breakpoint on a 150k+ row table. api.setNodesSelected() batches an entire
    // array into a single dispatch (confirmed by reading AG Grid's own
    // SelectionService.setNodesSelected, which loops nodes internally and calls
    // dispatchSelectionChanged only once at the end).
    const toSelect = [];
    const toDeselect = [];
    api.forEachNode((node) => {
      if (selectedRowIndexes.has(node.rowIndex)) {
        if (!node.isSelected()) toSelect.push(node);
      } else if (node.isSelected()) {
        toDeselect.push(node);
      }
    });
    if (toDeselect.length > 0) api.setNodesSelected({ nodes: toDeselect, newValue: false });
    if (toSelect.length > 0) api.setNodesSelected({ nodes: toSelect, newValue: true });
  };
  // Handle the visibility of the modal.
  // If `state.showModal` is false, sets it to true,
  // if is true, sets it to false.
  // handleToggleModal() {
  //   this.setState({ showModal: !this.state.showModal });
  // }
  handleValue(value) {
    this.increaseXPercent(value / 100);
    //this.setState({ showModal: !this.state.showModal });
  }

  handleNativeKeyDown = (event) => {
    // Check for Cmd on Mac or Ctrl on Windows/Linux
    const isModifierPressed = event.metaKey || event.ctrlKey;
    const isShiftPressed = event.shiftKey;
    const isArrowKey = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key);

    if (isModifierPressed && isArrowKey) {
      event.preventDefault();
      if (!this.gridApi) return;

      const previousCell = this.gridApi.getFocusedCell();
      if (!previousCell) return;

      const gridApi = this.gridApi;

      const isBlank = (rowIndex, col) => {
        if (!col) return true;
        const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
        if (!rowNode) return true;
        const value = rowNode.data[col.getColDef().field];
        return (
          value == null ||
          (typeof value === "string" && value.trim() === "") ||
          (typeof value === "object" && value !== null && Object.keys(value).length === 0)
        );
      };

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        const rowCount = gridApi.getDisplayedRowCount();
        let nextRowIndex = previousCell.rowIndex;
        const column = previousCell.column;
        const move = event.key === "ArrowDown" ? 1 : -1;
        const isNextCellBlank = isBlank(previousCell.rowIndex + move, column);

        if (!isNextCellBlank) {
          nextRowIndex = previousCell.rowIndex + move;
          for (let i = previousCell.rowIndex + move * 2; i >= 0 && i < rowCount; i += move) {
            if (isBlank(i, column)) break;
            nextRowIndex = i;
          }
        } else {
          for (let i = previousCell.rowIndex + move; i >= 0 && i < rowCount; i += move) {
            if (!isBlank(i, column)) {
              nextRowIndex = i;
              break;
            }
          }
        }

        if (nextRowIndex !== previousCell.rowIndex) {
          // Selection logic for Shift+Cmd/Ctrl+Arrow
          if (isShiftPressed) {
            const startRow = Math.min(previousCell.rowIndex, nextRowIndex);
            const endRow = Math.max(previousCell.rowIndex, nextRowIndex);
            gridApi.addCellRange({
              rowStartIndex: startRow,
              rowEndIndex: endRow,
              columns: [column.getColId()],
            });
          }

          gridApi.setFocusedCell(nextRowIndex, column);
          gridApi.ensureIndexVisible(nextRowIndex, "middle");
          setTimeout(() => gridApi.setFocusedCell(nextRowIndex, column), 0);
        }
      } else {
        // ArrowLeft or ArrowRight
        const allColumns = this.gridApi.getAllDisplayedColumns();
        const currentColIndex = allColumns.findIndex((col) => col.getColId() === previousCell.column.getColId());
        let nextColIndex = currentColIndex;
        const rowIndex = previousCell.rowIndex;
        const move = event.key === "ArrowRight" ? 1 : -1;
        const isNextCellBlank = isBlank(rowIndex, allColumns[currentColIndex + move]);

        if (!isNextCellBlank) {
          nextColIndex = currentColIndex + move;
          for (let i = currentColIndex + move * 2; i >= 0 && i < allColumns.length; i += move) {
            if (isBlank(rowIndex, allColumns[i])) break;
            nextColIndex = i;
          }
        } else {
          for (let i = currentColIndex + move; i >= 0 && i < allColumns.length; i += move) {
            if (!isBlank(rowIndex, allColumns[i])) {
              nextColIndex = i;
              break;
            }
          }
        }

        if (nextColIndex !== currentColIndex) {
          const nextColumn = allColumns[nextColIndex];

          // Selection logic for Shift+Cmd/Ctrl+Arrow
          if (isShiftPressed) {
            const startIdx = Math.min(currentColIndex, nextColIndex);
            const endIdx = Math.max(currentColIndex, nextColIndex);
            const columnsInRange = allColumns.slice(startIdx, endIdx + 1);
            gridApi.addCellRange({
              rowStartIndex: rowIndex,
              rowEndIndex: rowIndex,
              columns: columnsInRange.map((c) => c.getColId()),
            });
          }

          gridApi.setFocusedCell(rowIndex, nextColumn);
          gridApi.ensureColumnVisible(nextColumn);
          setTimeout(() => gridApi.setFocusedCell(rowIndex, nextColumn), 0);
        }
      }
    }
  };

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    // Dev/test-only, read-only lookup hook for e2e tests: rather than simulating a user
    // scrolling through the DOM to *find* a row (many seconds per lookup on a large
    // table), a test can query the grid's actual row data directly via a single
    // synchronous call. No mutation methods are exposed, and this whole block is
    // dead-code-eliminated from production builds (`webpack --mode production` inlines
    // process.env.NODE_ENV, the same mechanism React itself uses for stripping its own
    // dev-only code) — it can never ship, regardless of any runtime tampering.
    if (process.env.NODE_ENV !== "production") {
      window.__mdmGrid = {
        findRowIndex: (predicate) => {
          let found = null;
          params.api.forEachNode((node) => {
            if (found === null && predicate(node.data)) found = node.rowIndex;
          });
          return found;
        },
        getRowCount: () => params.api.getDisplayedRowCount(),
        getRowData: (rowIndex) => params.api.getDisplayedRowAtIndex(rowIndex)?.data ?? null,
        // Real row-selection count across the whole node tree, not just the rows
        // currently virtualized into the DOM — needed to verify a whole-table range
        // selection actually selected every row, not just the visible slice.
        getSelectedRowCount: () => params.api.getSelectedNodes().length,
        // Scrolls the viewport so the given row is actually rendered (AG Grid virtualizes
        // rows out of the DOM when off-screen). Doesn't touch data — pure viewport
        // navigation, same as clicking a scrollbar.
        scrollToRow: (rowIndex) => params.api.ensureIndexVisible(rowIndex, "middle"),
        // Builds the same whole-row cell range a real row-number-gutter drag produces
        // (spanning every displayed data column — see isWholeRowRange below), then lets
        // the real onCellSelectionChanged listener react to it exactly as it would for a
        // genuine drag. Lets e2e tests exercise a full-table row-number range select at
        // real production scale (hundreds of thousands of rows) without an actual
        // multi-minute mouse-drag-with-autoscroll gesture through virtualized rows.
        // Selection-only — does not touch row data.
        selectWholeRowRange: (startIndex, endIndex) => {
          const dataColumnIds = params.api
            .getAllDisplayedColumns()
            .filter((c) => c.getColId() !== ROW_NUMBERS_COLUMN_ID)
            .map((c) => c.getColId());
          params.api.addCellRange({ rowStartIndex: startIndex, rowEndIndex: endIndex, columns: dataColumnIds });
        },
      };
    }

    if (this.props.gridReady) {
      this.props.gridReady(params);
    }
  }
  onFirstDataRendered = (params) => {
    // const allColumnIds = params.columnApi.getAllColumns().map((col) => col.colId);
    params.api.autoSizeAllColumns();
    this.props.doFirstDataRendered?.(params);
  };

  checkRemove() {
    let selectedNodes = this.gridApi.getSelectedNodes().map((node) => {
      return { rowId: node.rowIndex, data: node.data };
    });

    this.props.removeRows(selectedNodes);
  }
  checkRemoveStatus() {
    let selectedNodes = this.gridApi.getSelectedNodes().map((node) => {
      return { rowId: node.rowIndex, data: node.data };
    });
    if (selectedNodes.length > 0) {
      return true;
    }
    return false;
  }
  duplicateRow = (params) => {
    if (params.node) {
      const newData = cloneDeep(params.node.data);
      delete newData.ID;
      newData.crudType = "insert";
      const currentIndex = params.node.rowIndex;

      if (this.props.onDuplicateRow) {
        this.props.onDuplicateRow(newData, currentIndex + 1);

        // Wait for the grid to update and then ensure the new row is visible
        setTimeout(() => {
          // this.gridApi.refreshClientSideRowModel();
          this.gridApi.ensureIndexVisible(currentIndex + 1, "middle");
        }, 0);
      }
    }
  };

  // Copy the full highlighted row(s) to the clipboard. Unlike the built-in
  // "copy" (which copies only the selected cells/range), this copies every cell
  // in the selected rows.
  //
  // A click-and-drag across cells in multiple rows builds a cell range, a
  // SEPARATE selection state from row selection (getSelectedNodes) — and a plain
  // click always row-selects the clicked row as a side effect, so by the time a
  // drag finishes, getSelectedNodes() already has exactly that one anchor row,
  // never empty. Checking "selection is empty" therefore never catches a drag;
  // the row it copied was that incidental anchor-row selection, not a fallback.
  // preferRangeOverSelection (below) prefers an active cell range whenever it
  // spans MORE rows than the current row selection already covers, so a genuine
  // drag selection wins; row-selection methods that already cover every row
  // (click / Shift+click / Ctrl+click) are unaffected.
  copyRow = (params) => {
    const api = params.api;
    let selectedNodes = api.getSelectedNodes();

    if (preferRangeOverSelection(api, selectedNodes?.length || 0)) {
      selectedNodes = api.getSelectedNodes();
    }

    // Right-clicking a row that isn't already part of the current selection
    // should act on just that row (matching the built-in cell-copy behavior
    // and standard OS/app convention), while right-clicking a row that IS
    // already part of a multi-row selection should leave that selection intact.
    const rightClickedNodeIsSelected = params.node && selectedNodes?.some((n) => n === params.node);

    if (!rightClickedNodeIsSelected && params.node) {
      api.deselectAll();
      params.node.setSelected(true);
    }
    selectedNodes = api.getSelectedNodes();
    api.copySelectedRowsToClipboard();

    // Snapshot the actual row data (not clipboard text) for "Paste Row", in visual
    // row order so a multi-row paste lands in the same relative order it was copied.
    lastCopiedRowSnapshot = {
      columnIds: getRowLayoutColumnIds(api),
      rows: [...selectedNodes].sort((a, b) => a.rowIndex - b.rowIndex).map((node) => cloneDeep(node.data)),
    };
  };

  // Paste the clipboard contents starting at the cell that was right-clicked.
  // pasteFromClipboard() targets the grid's focused cell, so explicitly focus
  // the right-clicked cell first — a right-click does not always move focus.
  pasteFromContextMenu = (params) => {
    const api = params.api;
    if (params.node && params.column) {
      api.setFocusedCell(params.node.rowIndex, params.column, params.node.rowPinned);
    }
    api.pasteFromClipboard();
  };

  // Paste the last "Copy Row" snapshot onto the right-clicked row (and, for a
  // multi-row copy, the rows below it), overwriting field values matched by column
  // id rather than by clicked-cell position. Unlike pasteFromContextMenu, this never
  // touches the system clipboard or the right-clicked column — every field lands in
  // its own column regardless of which cell was right-clicked. Only enabled (see
  // getContextMenuItems) when the destination grid's columns match the columns the
  // rows were copied from, so a paste can never silently scatter values into the
  // wrong fields of an unrelated table.
  pasteRow = (params) => {
    const api = params.api;
    const snapshot = lastCopiedRowSnapshot;
    // snapshot.rows.length: guards a snapshot recorded from an empty selection (e.g. a
    // programmatic copyRow call with no rows) — its column layout would still match
    // this grid and pass the menu's disabled check, but there'd be nothing to paste.
    if (!snapshot || !snapshot.rows.length || !params.node) return;
    if (!sameRowLayout(snapshot.columnIds, getRowLayoutColumnIds(api))) return;

    const startRowIndex = params.node.rowIndex;
    let appliedCount = 0;
    snapshot.rows.forEach((sourceData, offset) => {
      const targetNode = api.getDisplayedRowAtIndex(startRowIndex + offset);
      if (!targetNode) return;
      appliedCount++;
      snapshot.columnIds.forEach((colId) => {
        const column = api.getColumn(colId);
        const colDef = column?.getColDef();
        const field = colDef?.field;
        if (!column || !colDef || !field || !(field in sourceData)) return;
        // column.isCellEditable() (the same check ag-grid's own built-in "Cut" menu
        // item uses) handles both a boolean editable and a per-cell callback
        // ((params) => boolean) — a plain `colDef.editable === false` check would
        // miss the callback form, since a function is never === false.
        if (!column.isCellEditable(targetNode)) return;
        let newValue = sourceData[field];
        // AG Grid enforces a "number" cellDataType on setDataValue: passing a string
        // silently no-ops (returns false, no mutation, no cellValueChanged) instead of
        // coercing it. sourceData can hold a string here even for a number column —
        // e.g. a row still holding text typed/pasted in before ever round-tripping
        // through a save — so coerce it ourselves rather than silently dropping the
        // field (this is exactly how THRESHOLD_COUNT/PRE_PERIOD_MONTHS/PROGRAM_MONTHS
        // went missing from a pasted row while every text/date column pasted fine).
        // A blank string means "clear the cell", not zero — Number("") is 0, so
        // coerce blanks to null instead of parsing them.
        if (colDef.cellDataType === "number" && typeof newValue === "string") {
          if (newValue.trim() === "") {
            newValue = null;
          } else {
            const parsed = Number(newValue);
            if (!Number.isNaN(parsed)) newValue = parsed;
          }
        }
        if (targetNode.data[field] === newValue) return;
        targetNode.setDataValue(colId, newValue);
      });
    });

    if (appliedCount < snapshot.rows.length) {
      toast.warning(
        `Paste Row only applied ${appliedCount} of ${snapshot.rows.length} copied row(s) — not enough rows below the pasted row to fit the rest.`,
      );
    }
  };

  getContextMenuItems = async (params) => {
    // Reading the clipboard is async and permission-gated; if it's denied or
    // unsupported, fail safe by treating the clipboard as empty (Paste disabled)
    // rather than letting the menu throw or hang.
    let clipboardHasContent = false;
    try {
      const text = await navigator.clipboard.readText();
      clipboardHasContent = !!text && text.length > 0;
    } catch (e) {
      clipboardHasContent = false;
    }

    var result = [
      // {
      //   // custom item
      //   name: "Fill Down ",
      //   action: function() {
      //     params.context.thisComponent.fillDown();
      //   },
      //   cssClasses: ["redFont", "bold"]
      // },
      {
        // custom item
        name: "Fill Across ",
        action: function () {
          params.context.thisComponent.fillAcross();
        },
        cssClasses: ["redFont", "bold"],
      },
      {
        // custom item
        name: "Fill Selection ",
        action: function () {
          params.context.thisComponent.fillSelection();
        },
        cssClasses: ["redFont", "bold"],
      },
      {
        name: "Duplicate Row",
        action: () => {
          this.duplicateRow(params);
        },
        cssClasses: ["redFont", "bold"],
      },
      "separator",

      // built in copy item
      "copy",
      {
        // custom item — copies the entire highlighted row(s), not just cells
        name: "Copy Row",
        icon: clipboardCopyIcon,
        action: () => {
          this.copyRow(params);
        },
      },
      {
        // custom item — pastes clipboard contents at the right-clicked cell.
        // Disabled when the clipboard is empty (or unreadable).
        name: "Paste",
        icon: clipboardPasteIcon,
        shortcut: "Ctrl+V",
        disabled: !clipboardHasContent,
        action: () => {
          this.pasteFromContextMenu(params);
        },
      },
      {
        // custom item — overwrites the right-clicked row (and, for a multi-row
        // copy, the rows below it) with the last "Copy Row" snapshot, aligning
        // values by column id. Disabled unless that snapshot's column layout
        // matches this grid's, so it can't scatter values into an unrelated table.
        name: "Paste Row",
        icon: clipboardPasteIcon,
        disabled: !(
          lastCopiedRowSnapshot &&
          lastCopiedRowSnapshot.rows.length > 0 &&
          sameRowLayout(lastCopiedRowSnapshot.columnIds, getRowLayoutColumnIds(params.api))
        ),
        action: () => {
          this.pasteRow(params);
        },
      },
      {
        // custom item
        name: "Excel Export (.xlsx)",
        icon: excelExportIcon,
        action: function () {
          params.context.thisComponent.excelExport();
        },
      },
    ];

    return result;
  };
  getMainMenuItems(params) {
    //remove the tool panel from the menu
    let finalMenuItem = [];
    let itemsToExclude = "toolPanel";
    params.defaultItems.forEach(function (item) {
      if (itemsToExclude.indexOf(item) < 0) {
        finalMenuItem.push(item);
      }
    });
    return finalMenuItem;
  }

  render() {
    const { gridReady, doFirstDataRendered, removeRows, onDuplicateRow, ...gridProps } = this.props;

    //const { showModal } = this.state;
    return (
      <div
        ref={this.gridContainerRef}
        className="ag-theme-balham"
        style={{
          height: "100%",
          width: "100%",
        }}>
        <AgGridReact
          modules={gridModules}
          ref={this.agGrid}
          autoSizeStrategy={{ type: "smart" }}
          // columnSize={"autoSize"}
          // columnDefs={cloneDeep(this.props.currentTable)}
          // dashGridOptions={{ suppressColumnVirtualisation: true }}
          // enableSorting={true}
          // enableFilter={true}
          // onRowDataUpdated={this.onRowDataUpdated.bind(this)}
          onFirstDataRendered={this.onFirstDataRendered}
          rowNumbers={true}
          cellSelection={true}
          onCellSelectionChanged={this.onCellSelectionChanged}
          stopEditingWhenCellsLoseFocus={true}
          rowSelection={{
            mode: "multiRow",
            checkboxes: false,
            headerCheckbox: false,
            enableClickSelection: true,
            copySelectedRows: false,
          }}
          getMainMenuItems={this.getMainMenuItems}
          //singleClickEdit={true}
          context={{ thisComponent: this }}
          getContextMenuItems={this.getContextMenuItems}
          //allowContextMenuWithControlKey={false}
          onGridReady={this.onGridReady.bind(this)}
          processCellForClipboard={function (params) {
            if (params.value === undefined) {
              return null;
            }
            if (params.value === null) {
              return null;
            } else if (typeof params.value === "object") {
              return params.value.name;
            } else {
              return params.value;
            }
          }}
          processCellFromClipboard={(params) => {
            if (params.value === undefined) {
              return null;
            }
            const colDef = params.column.colDef;
            const isComboBox = colDef.cellEditor === "ComboBoxEditor";
            const isFkRenderer = colDef.cellRenderer === "FKCellRenderer";

            if (isComboBox || isFkRenderer) {
              // cellEditorParams can be a function or an object
              const editorParams =
                typeof colDef.cellEditorParams === "function"
                  ? colDef.cellEditorParams({ colDef, node: params.node, data: params.node?.data })
                  : colDef.cellEditorParams;
              const values = editorParams?.values || [];
              const searchStr = String(params.value).trim().toLowerCase();

              // Primary: case-insensitive match by display name (handles typed/pasted names).
              let match = values.find((item) => {
                if (item && typeof item === "object" && item.name !== undefined && item.name !== null) {
                  return String(item.name).toLowerCase() === searchStr;
                }
                if (typeof item === "string") {
                  return item.toLowerCase() === searchStr;
                }
                return false;
              });

              // Fallback: match by stored id. Cells rendered by FKCellRenderer show the
              // raw underlying value (the picklist id, e.g. a date like "2024-04-30"), not
              // the friendlier picklist name (e.g. "2024-Apr") — so copying one cell and
              // pasting into another of the same column copies the id, not the name.
              if (!match) {
                match = values.find(
                  (item) =>
                    item &&
                    typeof item === "object" &&
                    item.id !== null &&
                    item.id !== undefined &&
                    String(item.id).toLowerCase() === searchStr,
                );
              }

              if (match) {
                const field = colDef.field;
                const isFk = field.includes("FK");
                const stored = isFk
                  ? { id: match.id ?? null, name: match.name ?? "" }
                  : match.id === null || match.id === undefined
                    ? typeof match === "string"
                      ? match
                      : (match.name ?? "")
                    : match.id;

                // AG Grid may not call setDataValue/valueSetter reliably after
                // processCellFromClipboard when immutableData is enabled.
                // Write directly to node data so the value is always applied.
                if (params.node) {
                  const oldValue = params.node.data[field];
                  params.node.data[field] = stored;
                  this._pasteUpdates.push({
                    node: params.node,
                    column: params.column,
                    colDef,
                    field,
                    oldValue,
                    newValue: stored,
                  });
                }

                return stored;
              }

              // No match found — mark as invalid so the cell highlights
              console.warn("[paste] no match found, returning invalid");
              return { name: params.value, status: "invalid" };
            }

            // Plain (non-FK, non-combo) column, e.g. a text/number column like
            // POD_COUNT. AG Grid does not reliably apply the pasted value to node
            // data when immutableData is enabled, so write it directly and track
            // it for refresh + onCellValueChanged, exactly like the FK/combo path.
            const field = colDef.field;
            const stored =
              typeof params.value === "object" && params.value !== null ? params.value.name : params.value;

            if (params.node && field) {
              const oldValue = params.node.data[field];
              params.node.data[field] = stored;
              this._pasteUpdates.push({
                node: params.node,
                column: params.column,
                colDef,
                field,
                oldValue,
                newValue: stored,
              });
            }

            return stored;
          }}
          onPasteStart={() => {
            this._pasteUpdates = [];
            if (this.props.onPasteStart) this.props.onPasteStart();
          }}
          onPasteEnd={(e) => {
            if (this._pasteUpdates.length > 0) {
              const rowNodes = [...new Set(this._pasteUpdates.map((u) => u.node))];
              const columns = [...new Set(this._pasteUpdates.map((u) => u.column))];

              // Force cells to re-render with the updated node data
              e.api.refreshCells({ rowNodes, columns, force: true });

              // Fire onCellValueChanged so dirty tracking works correctly
              if (this.props.onCellValueChanged) {
                this._pasteUpdates.forEach(({ node, colDef, oldValue, newValue }) => {
                  this.props.onCellValueChanged({
                    data: node.data,
                    colDef,
                    oldValue,
                    newValue,
                    node,
                    api: e.api,
                    source: "paste",
                  });
                });
              }

              this._pasteUpdates = [];
            }
            if (this.props.onPasteEnd) this.props.onPasteEnd(e);
          }}
          {...gridProps}
        />
      </div>
    );
  }
}
